import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getCCForDepreciation,
    getDepreciationWefDate,
    getCCDepreciationValue,
    getDepreciationConfigGrid,
    saveCCDepValues,
} from '../../api/AccountsAPI/depreciationConfigAPI';

// ── Async Thunks ───────────────────────────────────────────────────────────────

/** Cost centre dropdown for the value form — merges the "not yet configured" and "already configured" pools */
export const fetchCCListForDepreciation = createAsyncThunk(
    'depreciationConfig/fetchCCListForDepreciation',
    async (_, { rejectWithValue }) => {
        try {
            const [addRes, updateRes] = await Promise.all([
                getCCForDepreciation('Add'),
                getCCForDepreciation('Update'),
            ]);
            const addList = addRes.data?.Data || [];
            const updateList = updateRes.data?.Data || [];
            const merged = new Map();
            [...addList, ...updateList].forEach(cc => merged.set(cc.CC_Code, cc));
            return Array.from(merged.values());
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load cost centre list');
        }
    }
);

/** Earliest allowed WEF date for a new percentage entry */
export const fetchDepreciationWefDate = createAsyncThunk(
    'depreciationConfig/fetchDepreciationWefDate',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getDepreciationWefDate();
            const list = res.data?.Data || [];
            return list[0]?.wefdate || '';
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load the WEF date');
        }
    }
);

/** Currently configured value for a selected cost centre (to prefill the value input) */
export const fetchCCDepreciationValue = createAsyncThunk(
    'depreciationConfig/fetchCCDepreciationValue',
    async (code, { rejectWithValue }) => {
        try {
            const res = await getCCDepreciationValue(code);
            const list = res.data?.Data || [];
            return list[0]?.ccvalue || '';
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load the current value');
        }
    }
);

/** Combined grid — split into per-CC value rows and percentage-history rows */
export const fetchDepreciationGrid = createAsyncThunk(
    'depreciationConfig/fetchDepreciationGrid',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getDepreciationConfigGrid();
            const list = res.data?.Data || [];
            return {
                valueList: list.filter(r => r.ReportType === 'Depriciation Value'),
                percentageList: list.filter(r => r.ReportType === 'Depriciation Percentage'),
            };
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load the depreciation grid');
        }
    }
);

/** Save a per-CC depreciation value */
export const submitDepreciationValue = createAsyncThunk(
    'depreciationConfig/submitDepreciationValue',
    async ({ CostCenter, Value, Createdby, RoleId }, { rejectWithValue }) => {
        try {
            const res = await saveCCDepValues({
                Id: 0,
                ReportType: 'Depreciation',
                CostCenter,
                Value,
                Createdby,
                RoleId,
                Action: 'Depvalue',
            });
            const status = res.data?.Data;
            if (status !== 'Submited') {
                return rejectWithValue(status || 'Failed to save the depreciation value');
            }
            return true;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to save the depreciation value');
        }
    }
);

/** Save a new WEF-dated depreciation percentage */
export const submitDepreciationPercentage = createAsyncThunk(
    'depreciationConfig/submitDepreciationPercentage',
    async ({ wefdate, InterestRate, Createdby, RoleId }, { rejectWithValue }) => {
        try {
            const res = await saveCCDepValues({
                Id: 0,
                ReportType: 'Depreciation',
                wefdate,
                InterestRate,
                Createdby,
                RoleId,
                Action: 'Deppercent',
            });
            const status = res.data?.Data;
            if (status !== 'Submited') {
                return rejectWithValue(status || 'Failed to save the depreciation percentage');
            }
            return true;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to save the depreciation percentage');
        }
    }
);

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    ccList: [],
    wefDate: '',
    currentCCValue: '',
    valueList: [],
    percentageList: [],
    valueSaveResult: null,
    percentageSaveResult: null,

    loading: {
        ccList: false,
        wefDate: false,
        ccValue: false,
        grid: false,
        saveValue: false,
        savePercentage: false,
    },
    errors: {
        ccList: null,
        wefDate: null,
        ccValue: null,
        grid: null,
        saveValue: null,
        savePercentage: null,
    },
};

const depreciationConfigSlice = createSlice({
    name: 'depreciationConfig',
    initialState,
    reducers: {
        clearCCDepreciationValue: (state) => { state.currentCCValue = ''; state.errors.ccValue = null; },
        clearDepreciationValueSaveResult: (state) => { state.valueSaveResult = null; state.errors.saveValue = null; },
        clearDepreciationPercentageSaveResult: (state) => { state.percentageSaveResult = null; state.errors.savePercentage = null; },
        resetDepreciationConfig: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCCListForDepreciation.pending, s => { s.loading.ccList = true; s.errors.ccList = null; })
            .addCase(fetchCCListForDepreciation.fulfilled, (s, a) => { s.loading.ccList = false; s.ccList = a.payload; })
            .addCase(fetchCCListForDepreciation.rejected, (s, a) => { s.loading.ccList = false; s.errors.ccList = a.payload; });

        builder
            .addCase(fetchDepreciationWefDate.pending, s => { s.loading.wefDate = true; s.errors.wefDate = null; })
            .addCase(fetchDepreciationWefDate.fulfilled, (s, a) => { s.loading.wefDate = false; s.wefDate = a.payload; })
            .addCase(fetchDepreciationWefDate.rejected, (s, a) => { s.loading.wefDate = false; s.errors.wefDate = a.payload; });

        builder
            .addCase(fetchCCDepreciationValue.pending, s => { s.loading.ccValue = true; s.errors.ccValue = null; })
            .addCase(fetchCCDepreciationValue.fulfilled, (s, a) => { s.loading.ccValue = false; s.currentCCValue = a.payload; })
            .addCase(fetchCCDepreciationValue.rejected, (s, a) => { s.loading.ccValue = false; s.errors.ccValue = a.payload; });

        builder
            .addCase(fetchDepreciationGrid.pending, s => { s.loading.grid = true; s.errors.grid = null; })
            .addCase(fetchDepreciationGrid.fulfilled, (s, a) => {
                s.loading.grid = false;
                s.valueList = a.payload.valueList;
                s.percentageList = a.payload.percentageList;
            })
            .addCase(fetchDepreciationGrid.rejected, (s, a) => { s.loading.grid = false; s.errors.grid = a.payload; });

        builder
            .addCase(submitDepreciationValue.pending, s => { s.loading.saveValue = true; s.errors.saveValue = null; s.valueSaveResult = null; })
            .addCase(submitDepreciationValue.fulfilled, (s) => { s.loading.saveValue = false; s.valueSaveResult = true; })
            .addCase(submitDepreciationValue.rejected, (s, a) => { s.loading.saveValue = false; s.errors.saveValue = a.payload; });

        builder
            .addCase(submitDepreciationPercentage.pending, s => { s.loading.savePercentage = true; s.errors.savePercentage = null; s.percentageSaveResult = null; })
            .addCase(submitDepreciationPercentage.fulfilled, (s) => { s.loading.savePercentage = false; s.percentageSaveResult = true; })
            .addCase(submitDepreciationPercentage.rejected, (s, a) => { s.loading.savePercentage = false; s.errors.savePercentage = a.payload; });
    },
});

export const {
    clearCCDepreciationValue,
    clearDepreciationValueSaveResult,
    clearDepreciationPercentageSaveResult,
    resetDepreciationConfig,
} = depreciationConfigSlice.actions;

// ── Selectors ──────────────────────────────────────────────────────────────────
export const selectDepCCList = s => s.depreciationConfig.ccList;
export const selectDepWefDate = s => s.depreciationConfig.wefDate;
export const selectDepCurrentCCValue = s => s.depreciationConfig.currentCCValue;
export const selectDepValueList = s => s.depreciationConfig.valueList;
export const selectDepPercentageList = s => s.depreciationConfig.percentageList;
export const selectDepValueSaveResult = s => s.depreciationConfig.valueSaveResult;
export const selectDepPercentageSaveResult = s => s.depreciationConfig.percentageSaveResult;

export const selectDepCCListLoading = s => s.depreciationConfig.loading.ccList;
export const selectDepWefDateLoading = s => s.depreciationConfig.loading.wefDate;
export const selectDepCCValueLoading = s => s.depreciationConfig.loading.ccValue;
export const selectDepGridLoading = s => s.depreciationConfig.loading.grid;
export const selectDepGridError = s => s.depreciationConfig.errors.grid;
export const selectDepSaveValueLoading = s => s.depreciationConfig.loading.saveValue;
export const selectDepSaveValueError = s => s.depreciationConfig.errors.saveValue;
export const selectDepSavePercentageLoading = s => s.depreciationConfig.loading.savePercentage;
export const selectDepSavePercentageError = s => s.depreciationConfig.errors.savePercentage;

export default depreciationConfigSlice.reducer;
